/**
 * BulkRowCapture — row-based bulk task entry.
 *
 * Renders a stack of editable task rows (spreadsheet-style cells) with
 * inline section dividers for "#Header" lines. The trailing row is always
 * empty and acts as the "type here for the next task" affordance — typing
 * a title and pressing Enter parses NLP into the cells and spawns a new
 * blank row below.
 *
 * State is a flat RowItem[] (task or header) — easier to mutate than the
 * nested BulkSection[] format. The flat list is converted to BulkSection[]
 * only when handing off to submit or to the Stage 2 review dialog.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Calendar, ClipboardPaste, Flag, FolderPlus, Lightbulb, Plus,
  Trash2, Zap,
} from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { Project } from '@/types'
import {
  applyParseToRow,
  makeEmptyTaskRow,
  makeHeaderRow,
  resolveProjectName,
  textToRows,
  type RowItem,
  type TaskRowItem,
  type HeaderRowItem,
  type EnergyLevel,
} from '@/utils/bulkTaskParser'

const PRIORITY_OPTIONS: Array<{ value: number; label: string; color: string }> = [
  { value: 5, label: 'Critical', color: '#ef4444' },
  { value: 4, label: 'High', color: '#f97316' },
  { value: 3, label: 'Medium', color: '#eab308' },
  { value: 2, label: 'Low', color: '#3b82f6' },
  { value: 1, label: 'Very Low', color: '#6b7280' },
]

const ENERGY_OPTIONS: Array<{ value: EnergyLevel; label: string }> = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
]

// Touch keyboards have no Tab/Esc — the keyboard-shortcut hints only add
// clutter on phones, so use a short placeholder there. Evaluated once at
// module load; a mid-session orientation flip keeping the long hint is fine.
const IS_NARROW_SCREEN =
  typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
const BULK_ROW_PLACEHOLDER = IS_NARROW_SCREEN
  ? 'Add a task… ("Report tomorrow 2h high")'
  : 'Add a task… (Tab = next cell, Enter = create + new row, paste many = explode)'

interface BulkRowCaptureProps {
  rows: RowItem[]
  onChange: (rows: RowItem[]) => void
  projects: Project[]
}

export function BulkRowCapture({ rows, onChange, projects }: BulkRowCaptureProps) {
  // Refs to each title input so we can focus programmatically after row
  // creation / NLP parse / deletion.
  const titleRefs = useRef<Map<string, HTMLInputElement>>(new Map())
  const focusOnNextRender = useRef<string | null>(null)

  useEffect(() => {
    if (focusOnNextRender.current) {
      const el = titleRefs.current.get(focusOnNextRender.current)
      if (el) {
        el.focus()
        // Place cursor at end
        const v = el.value
        try { el.setSelectionRange(v.length, v.length) } catch { /* select() not supported on all input types */ }
      }
      focusOnNextRender.current = null
    }
  })

  // ─── Row mutations ──────────────────────────────────────────────────────

  const updateTaskRow = useCallback((id: string, patch: Partial<TaskRowItem>) => {
    onChange(rows.map(r => {
      if (r.id !== id || r.kind !== 'task') return r
      return { ...r, ...patch }
    }))
  }, [rows, onChange])

  const updateHeaderRow = useCallback((id: string, patch: Partial<HeaderRowItem>) => {
    onChange(rows.map(r => {
      if (r.id !== id || r.kind !== 'header') return r
      return { ...r, ...patch }
    }))
  }, [rows, onChange])

  const removeRow = useCallback((id: string) => {
    const remaining = rows.filter(r => r.id !== id)
    // Always keep at least one empty task row so the user has somewhere to
    // type when the list is empty.
    if (remaining.every(r => r.kind === 'header') || remaining.length === 0) {
      remaining.push(makeEmptyTaskRow())
    }
    onChange(remaining)
  }, [rows, onChange])

  /**
   * The "spreadsheet" gesture: user types a title and presses Enter.
   *   1. If title starts with "#", convert this row to a section header.
   *   2. Otherwise, NLP-parse the title and update other cells.
   *   3. Either way, append a fresh empty row after this one and focus it.
   */
  const commitAndAdvance = useCallback((rowId: string) => {
    const row = rows.find(r => r.id === rowId)
    if (!row) return

    const updated = [...rows]
    const idx = updated.findIndex(r => r.id === rowId)

    if (row.kind === 'task') {
      const trimmed = row.title.trim()

      // Empty title? Just advance without creating anything.
      if (!trimmed) {
        // If this is already the last row, do nothing.
        if (idx === updated.length - 1) return
        // Otherwise just move focus to the next row's title.
        const nextRow = updated[idx + 1]
        focusOnNextRender.current = nextRow.id
        onChange(updated)
        return
      }

      // "#Header" → convert to header row
      const headerMatch = trimmed.match(/^#\s*(.+)$/)
      if (headerMatch) {
        const name = headerMatch[1].trim()
        const header = makeHeaderRow(name, projects)
        updated[idx] = header
      } else {
        // NLP parse + selective cell update
        updated[idx] = applyParseToRow(row)
      }
    }
    // (For header rows, Enter just appends a new task row below.)

    // Append a new empty task row after this one, focus its title.
    const fresh = makeEmptyTaskRow()
    updated.splice(idx + 1, 0, fresh)
    focusOnNextRender.current = fresh.id
    onChange(updated)
  }, [rows, projects, onChange])

  /**
   * Parse on Tab-out of the title cell (without creating a new row — Tab
   * is for moving between cells in the current row).
   */
  const parseTitleOnly = useCallback((rowId: string) => {
    const row = rows.find(r => r.id === rowId)
    if (!row || row.kind !== 'task') return
    if (!row.title.trim()) return
    // "#Header" → convert to header (same as Enter behaviour)
    const headerMatch = row.title.trim().match(/^#\s*(.+)$/)
    if (headerMatch) {
      const name = headerMatch[1].trim()
      onChange(rows.map(r => (r.id === rowId ? makeHeaderRow(name, projects) : r)))
      return
    }
    onChange(rows.map(r => (r.id === rowId ? applyParseToRow(r as TaskRowItem) : r)))
  }, [rows, projects, onChange])

  /**
   * Handle multi-line paste into a title cell: explode into N rows.
   */
  const handleTitlePaste = useCallback((rowId: string, e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text')
    if (!text.includes('\n')) return // single-line paste — let browser handle
    e.preventDefault()

    const newRows = textToRows(text, projects)
    if (newRows.length === 0) return

    const idx = rows.findIndex(r => r.id === rowId)
    if (idx < 0) return

    // Replace the current (empty) row with the parsed rows, or insert
    // after if the user already had content typed.
    const current = rows[idx]
    const wasEmpty = current.kind === 'task' && !current.title.trim()
    const before = rows.slice(0, idx)
    const after = wasEmpty ? rows.slice(idx + 1) : rows.slice(idx + 1)
    const keepCurrent = wasEmpty ? [] : [current]

    // Ensure trailing empty row
    const trailing: RowItem[] = after.length === 0 || after[after.length - 1].kind === 'header' ? [makeEmptyTaskRow()] : []

    const final = [...before, ...keepCurrent, ...newRows, ...after, ...trailing]
    onChange(final)
    // Focus the trailing empty (or the last pasted row if no trailing)
    const focusTarget = trailing[0]?.id ?? newRows[newRows.length - 1].id
    focusOnNextRender.current = focusTarget
  }, [rows, projects, onChange])

  /**
   * "Paste many" button — opens a prompt-style modal. For simplicity we
   * use a native window.prompt; if we want a nicer textarea overlay
   * later, swap it for a real dialog.
   */
  const handlePasteMany = useCallback(() => {
    const text = window.prompt('Paste many tasks (one per line, use "#Project" to split):')
    if (!text || !text.trim()) return
    const newRows = textToRows(text, projects)
    if (newRows.length === 0) return

    // Insert before the trailing empty row if there is one; otherwise append.
    const last = rows[rows.length - 1]
    const isTrailingEmpty = last && last.kind === 'task' && !last.title.trim()
    const trailing = isTrailingEmpty ? [last] : [makeEmptyTaskRow()]
    const before = isTrailingEmpty ? rows.slice(0, -1) : rows

    onChange([...before, ...newRows, ...trailing])
    focusOnNextRender.current = trailing[0].id
  }, [rows, projects, onChange])

  const handleAddRowButton = useCallback(() => {
    const fresh = makeEmptyTaskRow()
    onChange([...rows, fresh])
    focusOnNextRender.current = fresh.id
  }, [rows, onChange])

  // ─── Header chip click — quick resolution toggles ────────────────────────

  const setHeaderName = useCallback((id: string, name: string) => {
    const newName = name.trim()
    if (!newName) return
    updateHeaderRow(id, {
      headerName: newName,
      resolution: resolveProjectName(newName, projects),
    })
  }, [projects, updateHeaderRow])

  const acceptFuzzy = useCallback((rowId: string) => {
    const row = rows.find(r => r.id === rowId)
    if (!row || row.kind !== 'header') return
    const r = row.resolution
    if (r.kind !== 'fuzzy-suggestion') return
    updateHeaderRow(rowId, {
      resolution: {
        kind: 'user-mapped-existing',
        projectId: r.suggestedProjectId,
        rawName: r.rawName,
      },
    })
  }, [rows, updateHeaderRow])

  const confirmNew = useCallback((rowId: string) => {
    const row = rows.find(r => r.id === rowId)
    if (!row || row.kind !== 'header') return
    const r = row.resolution
    const rawName = r.kind === 'new' || r.kind === 'fuzzy-suggestion' ? r.rawName : row.headerName
    updateHeaderRow(rowId, { resolution: { kind: 'user-confirmed-new', rawName } })
  }, [rows, updateHeaderRow])

  // ─── Render helpers ──────────────────────────────────────────────────────

  const projectById = useMemo(() => new Map(projects.map(p => [p.id, p])), [projects])

  const renderHeaderRow = (row: HeaderRowItem) => {
    const r = row.resolution
    let chip = null
    if (r.kind === 'existing-exact' || r.kind === 'user-mapped-existing') {
      const pid = r.kind === 'existing-exact' ? r.projectId : r.projectId
      chip = (
        <Badge variant="secondary" className="text-xs">
          ✓ {projectById.get(pid)?.name ?? row.headerName}
        </Badge>
      )
    } else if (r.kind === 'fuzzy-suggestion') {
      const suggested = projectById.get(r.suggestedProjectId)
      chip = (
        <span className="inline-flex items-center gap-1.5 text-xs">
          <Badge variant="outline" className="text-amber-600 border-amber-500">
            <Lightbulb className="h-3 w-3 mr-1" /> Did you mean "{suggested?.name}"?
          </Badge>
          <Button size="sm" variant="outline" className="h-5 px-1.5 text-[10px]" onClick={() => acceptFuzzy(row.id)}>
            Yes
          </Button>
          <Button size="sm" variant="outline" className="h-5 px-1.5 text-[10px]" onClick={() => confirmNew(row.id)}>
            No, new
          </Button>
        </span>
      )
    } else if (r.kind === 'user-confirmed-new') {
      chip = (
        <Badge variant="outline" className="text-xs border-blue-500 text-blue-600 dark:text-blue-400">
          <FolderPlus className="h-3 w-3 mr-1" /> New: {r.rawName}
        </Badge>
      )
    } else if (r.kind === 'new') {
      chip = (
        <span className="inline-flex items-center gap-1.5 text-xs">
          <Badge variant="outline" className="text-amber-600 border-amber-500">
            <FolderPlus className="h-3 w-3 mr-1" /> "{r.rawName}" not found
          </Badge>
          <Button size="sm" variant="outline" className="h-5 px-1.5 text-[10px]" onClick={() => confirmNew(row.id)}>
            Create new
          </Button>
        </span>
      )
    }

    return (
      <div
        key={row.id}
        className="flex items-center gap-2 px-2 py-1.5 rounded border border-dashed border-muted-foreground/30 bg-muted/30"
      >
        <span className="text-xs font-semibold text-muted-foreground shrink-0">SECTION</span>
        <input
          type="text"
          value={row.headerName}
          onChange={(e) => setHeaderName(row.id, e.target.value)}
          className="flex-1 min-w-0 bg-transparent border-0 text-sm font-medium focus:outline-none focus:ring-0 px-1"
        />
        <div className="shrink-0">{chip}</div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
          onClick={() => removeRow(row.id)}
          title="Remove section divider"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    )
  }

  const renderTaskRow = (row: TaskRowItem, isLast: boolean) => {
    const currentPriority = PRIORITY_OPTIONS.find(p => p.value === row.priority)
    const hasContent = row.title.trim().length > 0

    return (
      <div
        key={row.id}
        className={cn(
          'group flex flex-wrap items-center gap-2 px-3 py-2 rounded border transition-colors md:flex-nowrap',
          hasContent
            ? 'border-primary/40 bg-primary/[0.02]'
            : 'border-dashed border-muted-foreground/30',
          !isLast && 'hover:border-muted-foreground/60'
        )}
      >
        <Plus className="hidden h-4 w-4 text-muted-foreground/60 shrink-0 md:block" />
        <input
          ref={(el) => {
            if (el) titleRefs.current.set(row.id, el)
            else titleRefs.current.delete(row.id)
          }}
          type="text"
          value={row.title}
          placeholder={isLast ? BULK_ROW_PLACEHOLDER : ''}
          onChange={(e) => updateTaskRow(row.id, { title: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitAndAdvance(row.id)
            } else if (e.key === 'Escape') {
              updateTaskRow(row.id, { title: '' })
            }
          }}
          onBlur={() => parseTitleOnly(row.id)}
          onPaste={(e) => handleTitlePaste(row.id, e)}
          className="flex-1 min-w-0 basis-full md:basis-0 bg-transparent border-0 text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-0 px-1"
        />

        <input
          type="date"
          value={row.deadline}
          onChange={(e) => updateTaskRow(row.id, { deadline: e.target.value })}
          title="Due date"
          className={cn(
            'w-[130px] text-xs bg-transparent rounded px-2 py-1',
            'border border-muted-foreground/20 hover:border-muted-foreground/40',
            'focus:outline-none focus:ring-1 focus:ring-primary',
            row.deadline ? 'text-foreground' : 'text-muted-foreground'
          )}
        />

        <Select value={String(row.priority)} onValueChange={(v) => updateTaskRow(row.id, { priority: Number(v) })}>
          <SelectTrigger className="w-[110px] h-7 text-xs">
            <SelectValue>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: currentPriority?.color }} />
                {currentPriority?.label}
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {PRIORITY_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={String(opt.value)}>
                <span className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: opt.color }} />
                  {opt.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={row.energy} onValueChange={(v) => updateTaskRow(row.id, { energy: v as EnergyLevel })}>
          <SelectTrigger className="w-[95px] h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ENERGY_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1">
          <input
            type="number"
            value={row.estimatedMinutes}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10)
              updateTaskRow(row.id, { estimatedMinutes: Number.isFinite(v) && v > 0 ? v : 1 })
            }}
            min={1}
            step={5}
            title="Estimated minutes"
            className={cn(
              'w-14 text-xs text-right bg-transparent rounded px-2 py-1',
              'border border-muted-foreground/20 hover:border-muted-foreground/40',
              'focus:outline-none focus:ring-1 focus:ring-primary',
              '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
            )}
          />
          <span className="text-xs text-muted-foreground">m</span>
        </div>

        {/* Delete button — only visible on hover, and never on the lone trailing empty row */}
        {(!isLast || hasContent) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-60 hover:opacity-100 text-muted-foreground hover:text-destructive"
            onClick={() => removeRow(row.id)}
            title="Delete this row"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    )
  }

  // Find the *last* task row (for the "isLast" placeholder treatment).
  const lastTaskRowIdx = (() => {
    for (let i = rows.length - 1; i >= 0; i--) {
      if (rows[i].kind === 'task') return i
    }
    return -1
  })()

  return (
    <div className="space-y-1.5">
      {rows.map((row, i) => (
        row.kind === 'header'
          ? renderHeaderRow(row)
          : renderTaskRow(row, i === lastTaskRowIdx)
      ))}

      {/* Action row — Add row + Paste many */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={handleAddRowButton}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add row
        </Button>
        <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={handlePasteMany} title="Paste multi-line text and explode into rows">
          <ClipboardPaste className="h-3.5 w-3.5 mr-1" /> Paste many lines
        </Button>
        <span className="text-[11px] text-muted-foreground ml-auto hidden items-center gap-2 md:flex">
          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> due</span>
          <span className="inline-flex items-center gap-1"><Flag className="h-3 w-3" /> priority</span>
          <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3" /> energy</span>
          <span>· #Project to split sections</span>
        </span>
      </div>
    </div>
  )
}

export default BulkRowCapture
